/**
 * The bank lines that could settle one investor's notice, ordered as the
 * matching engine reasons: payer IBAN + exact amount, then exact amount,
 * then by date. Unusable lines show why and don't select. Choosing collapses
 * the list so confirm stays in view.
 */

import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { sameToTheCent } from '@/utils/number-operations';
import type { BankIncomingAdminLine } from '@/types/bank-reconciliation';

interface BankLineCandidateListProps {
  lines: BankIncomingAdminLine[];
  remainingAmount: number;
  /** The call's currency. A line in any other cannot settle it. */
  currency: string;
  investorIban: string | null;
  selectedId: string | null;
  onSelect: (lineId: string) => void;
}

const foldIban = (value: string | null): string =>
  (value ?? '').replace(/\s+/g, '').toUpperCase();

/** What the backend books a line under when it states no currency. */
const ASSUMED_CURRENCY = 'EUR';

export function BankLineCandidateList({
  lines,
  remainingAmount,
  currency,
  investorIban,
  selectedId,
  onSelect,
}: BankLineCandidateListProps) {
  // Tracked here as well as in the parent so the list can collapse the moment
  // a line is chosen, without waiting to be re-rendered with a new prop.
  const [pickedId, setPickedId] = useState<string | null>(selectedId);
  const [showAll, setShowAll] = useState(false);

  // If the parent changes the selection out from under us, follow it - adjusted
  // during render rather than in an effect, which would cost a second pass.
  const [lastFromParent, setLastFromParent] = useState<string | null>(
    selectedId
  );
  if (selectedId !== lastFromParent) {
    setLastFromParent(selectedId);
    setPickedId(selectedId);
  }

  const wanted = foldIban(investorIban);

  // Only money coming in can settle a capital call.
  const credits = lines.filter((line) => line.amount > 0);

  const isPayersOwnAccount = (line: BankIncomingAdminLine) =>
    wanted !== '' && foldIban(line.counterparty_reference) === wanted;

  const isSameCurrency = (line: BankIncomingAdminLine) =>
    (line.currency ?? ASSUMED_CURRENCY).toUpperCase() ===
    currency.toUpperCase();

  // Only meaningful once the currencies agree: 5,000 dollars is not 5,000
  // euro, and the backend rejects the pair rather than converting.
  const isExactAmount = (line: BankIncomingAdminLine) =>
    isSameCurrency(line) && sameToTheCent(line.amount, remainingAmount);

  /** Why this line cannot settle the notice, or null when it can. */
  const cannotSettle = (line: BankIncomingAdminLine): string | null => {
    if (!isSameCurrency(line)) {
      return `in ${line.currency ?? ASSUMED_CURRENCY} - this call is in ${currency}`;
    }
    if (line.amount > remainingAmount && !isExactAmount(line)) {
      return 'more than the outstanding balance - split it in Incoming Transactions';
    }
    return null;
  };

  const rank = (line: BankIncomingAdminLine): number => {
    if (cannotSettle(line) !== null) return 4;
    const exact = isExactAmount(line);
    if (isPayersOwnAccount(line)) return exact ? 0 : 1;
    return exact ? 2 : 3;
  };

  const ordered = [...credits].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return (b.value_date ?? '').localeCompare(a.value_date ?? '');
  });

  const choose = (lineId: string) => {
    setPickedId(lineId);
    setShowAll(false);
    onSelect(lineId);
  };

  const picked = ordered.find((line) => line.id === pickedId) ?? null;
  const collapsed = picked !== null && !showAll;
  const visible = collapsed ? [picked] : ordered;

  if (ordered.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No unmatched bank transactions on this fund&apos;s accounts. Import a
        statement, or record the payment manually.
      </p>
    );
  }

  return (
    <div className="space-y-2" role="radiogroup" aria-label="Bank transaction">
      {visible.map((line) => {
        const blocked = cannotSettle(line);
        return (
          <label
            key={line.id}
            className={`flex gap-3 rounded-md border p-3 ${
              blocked ? 'opacity-60' : 'cursor-pointer hover:bg-muted/50'
            } ${pickedId === line.id ? 'border-primary bg-muted/40' : ''}`}
          >
            <input
              type="radio"
              name="bank-line"
              className="mt-1"
              disabled={blocked !== null}
              checked={pickedId === line.id}
              onChange={() => choose(line.id)}
            />
            <span className="min-w-0 flex-1 space-y-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="font-medium">
                  {formatCurrency(line.amount, {
                    currency: line.currency ?? undefined,
                  })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {line.value_date ? formatDate(line.value_date) : '-'}
                </span>
              </span>

              <span className="block truncate text-sm">
                {line.counterparty_name ?? 'Unknown payer'}
              </span>

              {line.remittance_information && (
                <span className="block truncate text-xs text-muted-foreground">
                  {line.remittance_information}
                </span>
              )}

              <span className="flex flex-wrap gap-1 pt-1">
                {isPayersOwnAccount(line) && (
                  <Badge variant="secondary">Payer&apos;s account</Badge>
                )}
                {isExactAmount(line) && <Badge>Exact amount</Badge>}
                {blocked === null &&
                  !isExactAmount(line) &&
                  line.amount < remainingAmount && (
                    <Badge variant="outline">Part payment</Badge>
                  )}
              </span>

              {/* Outside the badge row: as a flex item this refuses to shrink
                  below its own width and pushes the dialog into a scrollbar. */}
              {blocked && (
                <span className="block text-xs text-muted-foreground">
                  {blocked}
                </span>
              )}
            </span>
          </label>
        );
      })}

      {collapsed && ordered.length > 1 && (
        <Button
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={() => setShowAll(true)}
        >
          Choose a different transaction
        </Button>
      )}
    </div>
  );
}
